/**
 * Typing and helpers for the Intercom messenger.
 *
 * The Intercom JS library itself is loaded by the host page's snippet (in the
 * AngularJS shell's index.html), which lazily fetches the widget and exposes
 * the `window.Intercom` command queue. React owns everything else - booting
 * Intercom with the signed-in user's details and tracking the unread message
 * count - which previously lived in the AngularJS `intercom.run.js`. See
 * `useIntercom`.
 */

/** Intercom workspace (app) id. Matches the loader snippet in index.html. */
export const INTERCOM_APP_ID = 'kse39le3'

/** The per-user / per-company config passed to Intercom on boot and update. */
export interface IntercomBootConfig {
  app_id: string
  user_id?: string
  user_hash?: string
  name?: string
  email?: string
  company?: {
    company_id?: string
    name?: string
  }
}

/** Launcher and positioning settings, mirrored from the AngularJS config. */
interface IntercomSettings {
  hide_default_launcher: boolean
  custom_launcher_selector: string
  alignment: 'left' | 'right'
  vertical_padding: number
  horizontal_padding: number
  app_id: string
}

/**
 * The subset of the Intercom command API we use. Intercom is a single
 * overloaded function; we type each command shape we call.
 */
interface IntercomApi {
  (command: 'boot' | 'update', config?: IntercomBootConfig): void
  (command: 'show' | 'hide' | 'shutdown' | 'reattach_activator'): void
  (
    command: 'onUnreadCountChange',
    callback: (unreadCount: number) => void
  ): void
  (command: 'onShow' | 'onHide', callback: () => void): void
}

declare global {
  interface Window {
    Intercom?: IntercomApi
    intercomSettings?: IntercomSettings
  }
}

/**
 * Opens the Intercom messenger if the library has loaded. A no-op when
 * Intercom is unavailable (e.g. it failed to load) so callers can wire it to a
 * button without guarding first.
 */
export const showIntercom = (): void => {
  window.Intercom?.('show')
}
